<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <!-- Mobile -->
  <div v-if="isMobile" ref="mobileBuzzRef" class="orangehrm-buzz-mobile">
    <oxd-tab-container
      ref="swipeRef"
      v-model="tabSelector"
      :keep-alive="true"
      :show-tabs="false"
    >
      <oxd-tab-panel key="buzz_newsfeed" :name="$t('buzz.buzz_newsfeed')">
        <news-feed
          ref="newsFeedRef"
          :mobile="true"
          :employee="employee"
          :sort-field="sortField"
        >
          <post-filters
            :mobile="true"
            :filter="sortField"
            @update-priority="onUpdatePriority"
          ></post-filters>
        </news-feed>
      </oxd-tab-panel>
      <oxd-tab-panel
        key="buzz_anniversary"
        :name="$t('buzz.upcoming_anniversaries')"
      >
        <upcoming-anniversaries></upcoming-anniversaries>
      </oxd-tab-panel>
      <oxd-tab-panel key="buzz_birthday" :name="$t('buzz.upcoming_birthdays')">
        <upcoming-birthday></upcoming-birthday>
      </oxd-tab-panel>
      <oxd-tab-panel
        key="buzz_notifications"
        :name="$t('buzz.buzz_notifications')"
      >
        <buzz-notifications
          ref="notificationsRef"
          @unread-count="onUnreadCount"
          @go-to-feed="onGoToFeed"
        />
      </oxd-tab-panel>
    </oxd-tab-container>
    <nav class="orangehrm-buzz-mobile-nav" :aria-label="$t('general.buzz')">
      <button
        v-for="tab in mobileTabs"
        :key="tab.key"
        type="button"
        class="orangehrm-buzz-mobile-nav-item"
        :class="{'--active': isMobileTabActive(tab.name)}"
        :aria-label="tab.name"
        :aria-current="isMobileTabActive(tab.name) ? 'page' : undefined"
        @click="selectMobileTab(tab.name)"
      >
        <span class="orangehrm-buzz-mobile-nav-icon-wrap">
          <oxd-icon
            :name="tab.icon"
            class="orangehrm-buzz-mobile-nav-icon"
          ></oxd-icon>
          <span
            v-if="tab.key === 'buzz_notifications' && unreadCount > 0"
            class="orangehrm-buzz-mobile-nav-badge"
          >
            {{ unreadCount > 99 ? '99+' : unreadCount }}
          </span>
        </span>
        <span class="orangehrm-buzz-mobile-nav-label">{{
          tab.shortLabel
        }}</span>
      </button>
    </nav>
  </div>

  <!-- Medium Res -->
  <oxd-grid v-else-if="width < 1920" :cols="2" class="orangehrm-buzz-layout">
    <oxd-grid-item>
      <news-feed ref="newsFeedRef" :employee="employee" :sort-field="sortField">
        <post-filters
          :filter="sortField"
          @update-priority="onUpdatePriority"
        ></post-filters>
      </news-feed>
    </oxd-grid-item>
    <div class="buzz-right-component">
      <div class="side">
        <upcoming-anniversaries></upcoming-anniversaries>
        <div class="spacer"></div>
        <upcoming-birthday></upcoming-birthday>
      </div>
    </div>
  </oxd-grid>

  <!-- High Res -->
  <oxd-grid v-else :cols="3" class="orangehrm-buzz-layout">
    <oxd-grid-item>
      <post-filters
        :filter="sortField"
        @update-priority="onUpdatePriority"
      ></post-filters>
    </oxd-grid-item>
    <oxd-grid-item>
      <news-feed
        ref="newsFeedRef"
        :employee="employee"
        :sort-field="sortField"
      ></news-feed>
    </oxd-grid-item>
    <div class="buzz-right-component">
      <div class="side">
        <upcoming-anniversaries></upcoming-anniversaries>
        <div class="spacer"></div>
        <upcoming-birthday></upcoming-birthday>
      </div>
    </div>
  </oxd-grid>
  <topbar-notification-bell />
  <buzz-employee-mention-dialog />
</template>

<script>
import {
  OxdIcon,
  OxdTabPanel,
  DEVICE_TYPES,
  useResponsive,
  OxdTabContainer,
} from '@ohrm/oxd';
import {ref, computed, watch, nextTick, onMounted, onBeforeUnmount} from 'vue';
import usei18n from '@/core/util/composable/usei18n';
import useSwipe from '@/core/util/composable/useSwipe';
import {consumeBuzzNotificationTarget} from '@/orangehrmBuzzPlugin/util/buzzNotificationNavigation';
import fetchBuzzUnreadCount from '@/orangehrmBuzzPlugin/util/fetchBuzzUnreadCount';
import NewsFeed from '@/orangehrmBuzzPlugin/components/NewsFeed.vue';
import PostFilters from '@/orangehrmBuzzPlugin/components/PostFilters.vue';
import BuzzNotifications from '@/orangehrmBuzzPlugin/components/BuzzNotifications.vue';
import TopbarNotificationBell from '@/orangehrmBuzzPlugin/components/TopbarNotificationBell.vue';
import UpcomingAnniversaries from '@/orangehrmBuzzPlugin/components/UpcomingAnniversaries.vue';
import UpcomingBirthday from '@/orangehrmBuzzPlugin/components/UpcomingBirthday';
import BuzzEmployeeMentionDialog from '@/orangehrmBuzzPlugin/components/BuzzEmployeeMentionDialog';

export default {
  components: {
    'oxd-icon': OxdIcon,
    'upcoming-birthday': UpcomingBirthday,
    'news-feed': NewsFeed,
    'oxd-tab-panel': OxdTabPanel,
    'post-filters': PostFilters,
    'oxd-tab-container': OxdTabContainer,
    'upcoming-anniversaries': UpcomingAnniversaries,
    'buzz-notifications': BuzzNotifications,
    'topbar-notification-bell': TopbarNotificationBell,
    'buzz-employee-mention-dialog': BuzzEmployeeMentionDialog,
  },

  props: {
    employee: {
      type: Object,
      required: true,
    },
  },

  setup() {
    const {$t} = usei18n();
    const tabSelector = ref(null);
    const mobileBuzzRef = ref(null);
    const responsiveState = useResponsive();
    const mobileScrollResetTabKeys = new Set([
      'buzz_notifications',
      'buzz_anniversary',
      'buzz_birthday',
    ]);
    const sortField = ref('share.createdAtUtc');
    const notificationsRef = ref(null);
    const newsFeedRef = ref(null);
    const unreadCount = ref(0);
    const tabKeys = [
      $t('buzz.buzz_newsfeed'),
      $t('buzz.upcoming_anniversaries'),
      $t('buzz.upcoming_birthdays'),
      $t('buzz.buzz_notifications'),
    ];
    const mobileTabs = computed(() => [
      {
        key: 'buzz_newsfeed',
        name: $t('buzz.buzz_newsfeed'),
        shortLabel: $t('buzz.buzz_newsfeed_short'),
        icon: 'newspaper',
      },
      {
        key: 'buzz_notifications',
        name: $t('buzz.buzz_notifications'),
        shortLabel: $t('buzz.buzz_notifications_short'),
        icon: 'bell',
      },
      {
        key: 'buzz_anniversary',
        name: $t('buzz.upcoming_anniversaries'),
        shortLabel: $t('buzz.upcoming_anniversaries_short'),
        icon: 'award',
      },
      {
        key: 'buzz_birthday',
        name: $t('buzz.upcoming_birthdays'),
        shortLabel: $t('buzz.upcoming_birthdays_short'),
        icon: 'gift',
      },
    ]);
    const isMobile = computed(() => {
      return !(
        responsiveState.screenType === DEVICE_TYPES.DEVICE_LG ||
        responsiveState.screenType === DEVICE_TYPES.DEVICE_XL
      );
    });

    const width = computed(() => responsiveState.windowWidth);
    const currentTabIndex = computed(() =>
      tabKeys.findIndex(
        (key) => $t(key) === tabSelector.value || key === tabSelector.value,
      ),
    );
    const {swipeContainer} = useSwipe(($event) => {
      const direction = $event.offsetDirection;

      if (direction === 4) {
        // swipe left (go to previous tab)
        if (currentTabIndex.value > 0) {
          tabSelector.value =
            $t(tabKeys[currentTabIndex.value - 1]) ||
            tabKeys[currentTabIndex.value - 1];
        }
      }

      if (direction === 2) {
        // swipe right (go to next tab)
        if (currentTabIndex.value < tabKeys.length - 1) {
          tabSelector.value =
            $t(tabKeys[currentTabIndex.value + 1]) ||
            tabKeys[currentTabIndex.value + 1];
        }
      }
    });

    const isMobileTabActive = (name) => {
      return (
        tabSelector.value === name ||
        (tabSelector.value === null && name === mobileTabs.value[0]?.name)
      );
    };

    const getMobileTabKey = (tabName) =>
      mobileTabs.value.find((tab) => tab.name === tabName)?.key;

    const resetMobileBuzzScroll = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      const layoutContext = document.querySelector('.oxd-layout-context');
      if (layoutContext) {
        layoutContext.scrollTop = 0;
      }

      const mobileRoot = mobileBuzzRef.value;
      if (!mobileRoot) {
        return;
      }

      mobileRoot
        .querySelectorAll(
          '.orangehrm-buzz-anniversary-content, .orangehrm-buzz-birthday-content, .orangehrm-buzz-notifications, .orangehrm-buzz-notifications-list',
        )
        .forEach((element) => {
          element.scrollTop = 0;
        });
    };

    const selectMobileTab = (name) => {
      tabSelector.value = name;
      if (mobileScrollResetTabKeys.has(getMobileTabKey(name))) {
        nextTick(resetMobileBuzzScroll);
      }
    };

    const onUpdatePriority = ($event) => {
      if ($event) sortField.value = $event;
    };

    const onUnreadCount = (count) => {
      unreadCount.value = count;
    };

    const refreshMobileUnreadCount = () => {
      if (isMobile.value) {
        fetchBuzzUnreadCount().then((count) => {
          unreadCount.value = count;
        });
      }
    };

    const onGoToFeed = (notification) => {
      const shareId = notification?.share?.id;
      if (!shareId) {
        return;
      }

      if (isMobile.value) {
        tabSelector.value = $t('buzz.buzz_newsfeed');
      }

      nextTick(() => {
        newsFeedRef.value?.goToShare?.({
          shareId,
          commentId: notification?.comment?.id ?? null,
        });
      });
    };

    const applyBuzzNotificationTarget = async (target) => {
      if (!target?.shareId) {
        return;
      }

      if (isMobile.value) {
        tabSelector.value = $t('buzz.buzz_newsfeed');
      }

      await nextTick();

      for (let attempt = 0; attempt < 30; attempt++) {
        if (newsFeedRef.value?.goToShare) {
          await newsFeedRef.value.goToShare({
            shareId: target.shareId,
            commentId: target.commentId ?? null,
          });
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    };

    const onTopbarNotificationTarget = (event) => {
      const notification = event?.detail?.notification;
      if (notification) {
        onGoToFeed(notification);
      }
    };

    onMounted(() => {
      refreshMobileUnreadCount();

      const pendingTarget = consumeBuzzNotificationTarget();
      if (pendingTarget) {
        applyBuzzNotificationTarget(pendingTarget);
      }

      window.addEventListener(
        'orangehrm-buzz-notification-target',
        onTopbarNotificationTarget,
      );
    });

    watch(isMobile, (mobile) => {
      if (mobile) {
        refreshMobileUnreadCount();
      }
    });

    onBeforeUnmount(() => {
      window.removeEventListener(
        'orangehrm-buzz-notification-target',
        onTopbarNotificationTarget,
      );
    });

    watch(tabSelector, (value) => {
      if (value === $t('buzz.buzz_notifications')) {
        notificationsRef.value?.refresh?.();
      }

      if (
        isMobile.value &&
        mobileScrollResetTabKeys.has(getMobileTabKey(value))
      ) {
        nextTick(resetMobileBuzzScroll);
      }
    });

    return {
      width,
      isMobile,
      mobileTabs,
      unreadCount,
      sortField,
      tabSelector,
      mobileBuzzRef,
      notificationsRef,
      newsFeedRef,
      onUpdatePriority,
      onUnreadCount,
      onGoToFeed,
      isMobileTabActive,
      selectMobileTab,
      swipeRef: swipeContainer,
    };
  },
};
</script>

<style src="./view-buzz.scss" lang="scss" scoped></style>
